import { memo, useCallback, useState } from 'react'
import { FormControl, Menu, MenuItem } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { timeIntervalValues } from '../../../util/tradePage'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import { selectOptionChainTimeInterval } from '../../../selectors/home'
import commonActions from '../../../actions/common.actions'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { TimeBucket } from '../../../proto/generated/common'
import { OptionChainViewKey } from '../../../assets/constants'

type Props = {
    optionChainViewKey?: OptionChainViewKey
    orderOptionChain?: boolean
}

const OptionChainTimeIntervalMenuBody = ({
    optionChainViewKey: optionChainViewKeyProp,
    orderOptionChain = false,
}: Props) => {
    const optionChainViewKey = optionChainViewKeyProp ?? OptionChainViewKey.LIVE
    const chainType = orderOptionChain || optionChainViewKey === OptionChainViewKey.WIDGET ? 'extended' : 'normal'
    const timeInterval = useSelector(selectOptionChainTimeInterval(optionChainViewKey, chainType))
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const dispatch = useDispatch()

    const selectedLabel =
        timeIntervalValues.find(
            (t) => t.value.value === timeInterval.value && t.value.timeSeries === timeInterval.timeSeries
        )?.label ?? ''

    const handleTimeIntervalChange = useCallback(
        (value: TimeBucket) => {
            if (value.timeSeries !== timeInterval.timeSeries || value.value !== timeInterval.value) {
                commonActions.setOptionChainTimeInterval(dispatch, {
                    timeBucket: value,
                    optionChainViewKey,
                    type: chainType,
                })
            }
            AnalyticsManager.logEvent('Option Chain Time Interval Changed', { selected_value: value })
            setAnchorEl(null)
        },
        [dispatch, timeInterval, optionChainViewKey, chainType]
    )

    if (!selectedLabel) return null

    return (
        <FormControl sx={{ flexShrink: 0 }}>
            <NonInteractiveTooltip placement="top" title="Change time interval" arrow>
                <ExtraSmallButton
                    size="small"
                    variant="text"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    color="inherit"
                    sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', fontSize: '14px', minWidth: 0 }}
                >
                    {selectedLabel}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {timeIntervalValues.map((t) => (
                    <MenuItem
                        key={t.label}
                        selected={
                            t.value.value === timeInterval.value && t.value.timeSeries === timeInterval.timeSeries
                        }
                        onClick={() => handleTimeIntervalChange(t.value)}
                    >
                        {t.label}
                    </MenuItem>
                ))}
            </Menu>
        </FormControl>
    )
}

export default memo(OptionChainTimeIntervalMenuBody)
